import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Eye, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import CoutureLayout from "./CoutureLayout";
import { api, formatPrice, formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export default function CoutureOrders() {
    const { t } = useI18n();
    const [orders, setOrders] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        const params = statusFilter ? { status: statusFilter } : {};
        api.get("/couture/orders", { params })
            .then((r) => setOrders(r.data))
            .catch(() => toast.error(t("couture.load_error")))
            .finally(() => setLoading(false));
    };

    useEffect(load, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const remove = async (id) => {
        if (!window.confirm(t("couture.confirm_delete"))) return;
        try {
            await api.delete(`/couture/orders/${id}`);
            setOrders((prev) => prev.filter((o) => o.id !== id));
            toast.success(t("couture.deleted"));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10" data-testid="couture-orders-page">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl text-brand-navy">{t("couture.orders")}</h1>
                        <div className="w-12 h-px bg-brand-gold mt-2" />
                    </div>
                    <Link
                        to="/couturier/commandes/nouvelle"
                        data-testid="couture-orders-new"
                        className="inline-flex items-center gap-2 px-5 py-3 bg-brand-gold hover:bg-brand-gold-deep text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <PlusCircle className="w-4 h-4" /> {t("couture.new_order")}
                    </Link>
                </div>

                <div className="flex gap-2 mb-6 flex-wrap" data-testid="couture-status-filters">
                    {["", "en_cours", "pret", "livre", "annule"].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setStatusFilter(s)}
                            data-testid={`couture-filter-${s || "all"}`}
                            className={`px-4 py-2 text-[0.68rem] tracking-[0.16em] uppercase font-semibold transition-colors ${
                                statusFilter === s ? "bg-brand-navy text-white" : "bg-white border border-gray-200 text-gray-500 hover:border-brand-gold"
                            }`}
                        >
                            {s ? t(`couture.status_${s}`) : t("couture.filter_all")}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-gray-400">{t("admin.loading")}</div>
                ) : orders.length === 0 ? (
                    <div className="bg-white border border-gray-100 p-10 text-center text-gray-400" data-testid="couture-orders-empty">
                        {t("couture.no_orders")}
                    </div>
                ) : (
                    <div className="bg-white border border-gray-100 overflow-x-auto" data-testid="couture-orders-table">
                        <table className="w-full text-sm min-w-[760px]">
                            <thead>
                                <tr className="text-left text-[0.62rem] tracking-[0.18em] uppercase text-gray-400 border-b border-gray-100">
                                    <th className="px-5 py-4">{t("couture.tracking_code")}</th>
                                    <th className="px-5 py-4">{t("couture.client")}</th>
                                    <th className="px-5 py-4">{t("couture.category")}</th>
                                    <th className="px-5 py-4">{t("couture.stage")}</th>
                                    <th className="px-5 py-4">{t("couture.price")}</th>
                                    <th className="px-5 py-4">{t("couture.balance")}</th>
                                    <th className="px-5 py-4">{t("couture.status")}</th>
                                    <th className="px-5 py-4 text-right">{t("couture.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((o) => (
                                    <tr key={o.id} className="border-b border-gray-50 hover:bg-brand-paper/60 transition-colors" data-testid={`couture-order-row-${o.tracking_code}`}>
                                        <td className="px-5 py-4 font-mono text-xs text-brand-gold font-semibold">{o.tracking_code}</td>
                                        <td className="px-5 py-4">
                                            <div className="font-semibold text-brand-navy">{o.client_name}</div>
                                            <div className="text-xs text-gray-400">{o.client_phone}</div>
                                        </td>
                                        <td className="px-5 py-4 text-gray-600">
                                            {o.category_name || "—"}
                                            {o.subcategory_name ? ` / ${o.subcategory_name}` : ""}
                                        </td>
                                        <td className="px-5 py-4 text-gray-600">{t(`couture.stage_${o.stage}`)}</td>
                                        <td className="px-5 py-4 text-gray-600">{formatPrice(o.price_fcfa, "FCFA")}</td>
                                        <td className="px-5 py-4">
                                            <span className={o.balance_fcfa > 0 ? "text-brand-gold-deep font-semibold" : "text-emerald-600 font-semibold"}>
                                                {formatPrice(o.balance_fcfa, "FCFA")}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className="inline-block px-2.5 py-1 text-[0.62rem] tracking-[0.14em] uppercase font-semibold bg-brand-paper text-brand-navy border border-gray-200">
                                                {t(`couture.status_${o.status}`)}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <Link
                                                    to={`/couturier/commandes/${o.id}`}
                                                    data-testid={`couture-order-open-${o.tracking_code}`}
                                                    className="p-2 text-brand-navy hover:text-brand-gold transition-colors"
                                                    title={t("couture.open")}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Link>
                                                <button
                                                    onClick={() => remove(o.id)}
                                                    data-testid={`couture-order-delete-${o.tracking_code}`}
                                                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                                    title={t("couture.delete")}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </CoutureLayout>
    );
}
